import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    LucideIcon,
    Home,
    Calendar,
    LayoutList,
    LogOut, BookCopy,
    ChevronDown, UsersRound,
    Settings,
    FileSpreadsheet,
    Activity,
    History,
    PanelLeftClose,
    PanelLeftOpen,
    Loader2
} from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import { styleConfig } from '../../constants/design';
import { useAppState } from '../../context/AppStateContext';
import NotificationDropdown from './NotificationDropdown';
import KoMoldLogo from '../brand/KoMoldLogo';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';

/**
 * Does `target` name the page we are on?
 *
 * The board sub-menu items differ only by `?boardType=`, and both matchers in
 * play got that wrong: NavLink's own `isActive` compares pathname and ignores
 * the query, so all three lit up at once, and the hand-rolled check compared
 * `location.pathname` against a `to` that carries a query, so it never matched
 * and never corrected it.
 *
 * A target's query params are treated as requirements, not as an exact match:
 * `/dashboard` stays active on `/dashboard?boardType=repair` (it is the group's
 * own page), while `/dashboard?boardType=repair` does not match a different
 * boardType.
 */
const isLinkActive = (target: string, pathname: string, search: string): boolean => {
    const [targetPath, targetQuery] = target.split('?');
    // Descendant routes keep their section lit: /molds/1 is still ทะเบียนแม่พิมพ์.
    // This is what NavLink does by default, and dropping it stranded every
    // detail page with no active menu item at all. The charAt guard is what
    // stops '/' from claiming every route, and '/mold' from claiming
    // '/mold-orders'.
    const pathMatches =
        pathname === targetPath ||
        (pathname.startsWith(targetPath) && pathname.charAt(targetPath.length) === '/');
    if (!pathMatches) return false;
    if (!targetQuery) return true;
    const current = new URLSearchParams(search);
    return Array.from(new URLSearchParams(targetQuery)).every(
        ([key, value]) => current.get(key) === value,
    );
};

export interface NavItem {
    /**
     * Routes that belong to this section but do not live under `to`, so
     * descendant matching cannot find them — e.g. creating a mold is at
     * '/mold/create' (singular), not under '/molds'. Matched as prefixes, so
     * '/questionnaires' covers '/questionnaires/:id'.
     */
    matchPaths?: string[];
    icon: LucideIcon;
    label: string;
    to: string;
    children?: NavItem[];
    roles?: string[];
    showInMobile?: boolean;
}

interface SidebarNavItemProps extends NavItem {
    isExpanded: boolean;
    depth?: number;
    onNavigate?: () => void;
}

/**
 * A single nav row. Active state is a solid ink block — neutral, not blue: the accent
 * is reserved for primary actions so "you are here" and "do this" never compete.
 */
const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
    icon: Icon,
    label,
    to,
    matchPaths,
    isExpanded,
    children,
    depth = 0,
    onNavigate,
}) => {
    // The tooltip used to be an absolutely-positioned child of the row, which the
    // nav's own `overflow-y-auto` clipped away — the rail had no working labels.
    // It is positioned against the viewport instead, from the row's live rect.
    const [tip, setTip] = useState<{ top: number; left: number } | null>(null);
    const location = useLocation();
    const hasChildren = children && children.length > 0;
    const hasActiveChild = Boolean(
        hasChildren && children.some((child) => isLinkActive(child.to, location.pathname, location.search)),
    );
    // Land on a board and its group is already open, so the highlighted row is
    // on screen rather than folded away behind a closed parent.
    const [isOpen, setIsOpen] = useState(hasActiveChild);

    const showTip = (e: React.SyntheticEvent<HTMLElement>) => {
        if (isExpanded) return;
        const r = e.currentTarget.getBoundingClientRect();
        // Anchored to the rail's edge, not the row's: the row is 40px inside a
        // 60px rail, so measuring from the row put the tooltip back under it.
        const railRight = e.currentTarget.closest('aside')?.getBoundingClientRect().right ?? r.right;
        setTip({ top: r.top + r.height / 2, left: railRight + 8 });
    };
    const hideTip = () => setTip(null);

    const selfActive =
        isLinkActive(to, location.pathname, location.search) ||
        (matchPaths ?? []).some((path) => isLinkActive(path, location.pathname, location.search));
    // With the group open the child carries the highlight on its own — two dark
    // rows read as two separate destinations. Closed, or collapsed to the rail,
    // the children are not on screen, so the parent stands in for them.
    const childrenVisible = isExpanded && isOpen;
    const isActive = hasActiveChild && childrenVisible ? false : selfActive || hasActiveChild;

    const handleClick = (e: React.MouseEvent) => {
        if (hasChildren && isExpanded) {
            e.preventDefault();
            setIsOpen(!isOpen);
            return;
        }
        // On phones the sidebar is an overlay: leaving it open on top of the page
        // someone just navigated to means a second tap to get at anything.
        onNavigate?.();
    };

    return (
        <>
            <Link
                to={to}
                aria-current={isActive ? 'page' : undefined}
                onClick={handleClick}
                onMouseEnter={showTip}
                onMouseLeave={hideTip}
                onFocus={showTip}
                onBlur={hideTip}
                // The rail shows no text, so the label has to reach assistive tech
                // some other way. `title` is gone: it duplicated the tooltip below
                // with a second, slower native one.
                aria-label={isExpanded ? undefined : label}
                className={`
                    group relative flex items-center rounded-lg text-[14px] leading-[1.6] transition-colors duration-150 md:text-[13px]
                    ${isExpanded ? 'h-12 gap-[11px] px-[11px] md:h-[38px]' : 'h-10 w-10 justify-center'}
                    ${isActive
                        ? 'bg-[#0E1419] font-medium text-white'
                        : 'text-[#4E5964] hover:bg-[#0E1419]/[0.055]'
                    }
                `}
            >
                <Icon
                    className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[#7FA8DB]' : 'text-[#8892A0]'}`}
                    strokeWidth={1.75}
                />

                {isExpanded && (
                    <>
                        <span className="flex-1 truncate">{label}</span>
                        {hasChildren && (
                            <ChevronDown
                                className={`h-3.5 w-3.5 text-[#8892A0] transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                            />
                        )}
                    </>
                )}

                {/* Collapsed rail: the label is a tooltip, not a second line under the icon */}
                {!isExpanded && tip && (
                    <span
                        role="tooltip"
                        style={{ top: tip.top, left: tip.left }}
                        className="pointer-events-none fixed z-[60] -translate-y-1/2 whitespace-nowrap rounded-md bg-[#0E1419] px-2 py-1 text-[11.5px] text-white shadow-[0_4px_12px_rgba(14,20,25,0.18)]"
                    >
                        {label}
                    </span>
                )}
            </Link>

            {hasChildren && isOpen && isExpanded && (
                <div className="ml-3 flex flex-col gap-px border-l-2 border-[#D3D9DF] pl-[15px]">
                    {children.map((child) => (
                        <SidebarNavItem
                            key={child.to}
                            {...child}
                            isExpanded={isExpanded}
                            depth={depth + 1}
                            onNavigate={onNavigate}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

interface ModernSidebarProps {
    isOpen?: boolean;
    onNavigate?: () => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    /** Only desktop has a rail to collapse to; on phones the sidebar is a drawer. */
    canCollapse?: boolean;
}

const ModernSidebar: React.FC<ModernSidebarProps> = ({
    isOpen = false,
    isCollapsed = true,
    onToggleCollapse,
    canCollapse = true,
    onNavigate
}) => {
    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const { user, logout } = useAuth();
    const { appName } = useAppState();
    const accountLocation = useLocation();
    // The account row is two links to the same page (avatar and name), so it
    // announced aria-current twice on /profile — and showed no active state at
    // all, leaving the profile page the one screen with nothing lit.
    const isProfileActive = isLinkActive(
        ROUTES.MY_PROFILE,
        accountLocation.pathname,
        accountLocation.search,
    );

    const version = process.env.VITE_APP_VERSION;
    const commitHash = process.env.VITE_APP_COMMIT_HASH?.substring(0, 7);

    const isExpanded = isCollapsed !== undefined ? !isCollapsed : !internalCollapsed;

    const handleToggleCollapse = () => {
        if (onToggleCollapse) {
            onToggleCollapse();
        } else {
            setInternalCollapsed(!internalCollapsed);
        }
    };

    const getNavItems = (): NavItem[] => {
        return [
            { icon: Home, label: 'หน้าแรก', to: '/', showInMobile: true },
            {
                icon: Calendar,
                label: 'บอร์ดใบงาน',
                to: ROUTES.DASHBOARD,
                showInMobile: true,
                children: [
                    { icon: styleConfig.maintenance.icon, label: 'บอร์ดบำรุงรักษา', to: `${ROUTES.DASHBOARD}?boardType=maintenance` },
                    { icon: styleConfig.repair.icon, label: 'บอร์ดใบซ่อม', to: `${ROUTES.DASHBOARD}?boardType=repair` },
                    { icon: styleConfig.newMold.icon as LucideIcon, label: 'ใบรับแม่พิมพ์', to: `${ROUTES.DASHBOARD}?boardType=mold_acceptance` },
                ]
            },
            {
                icon: LayoutList,
                label: 'ทะเบียนแม่พิมพ์',
                to: ROUTES.MOLDS,
                // Creating a mold lives at '/mold/create' — singular, so
                // descendant matching off '/molds' cannot reach it.
                matchPaths: ['/mold/create'],
            },
            { icon: BookCopy, label: 'ทะเบียนใบงาน', to: ROUTES.MOLD_ORDERS },
            { icon: History, label: 'ประวัติสถานะใบงาน', to: ROUTES.STATE_ACTIVITIES },
            { icon: UsersRound, label: 'ทะเบียนผู้ใช้งาน', to: ROUTES.USERS, roles: ['ADMIN'] },
            { icon: FileSpreadsheet, label: 'ส่งออกใบงาน (CSV)', to: ROUTES.ORDER_EXPORT, roles: ['ADMIN'] },
            { icon: Settings, label: 'ตั้งค่าระบบ', to: ROUTES.CONFIGURATION, roles: ['ADMIN'], showInMobile: true },
            { icon: Activity, label: 'สถานะระบบ', to: ROUTES.SYSTEM_STATUS, roles: ['ADMIN'] },
        ];
    };

    const navItems = useMemo(() => {
        return getNavItems().filter((item: NavItem) =>
            !item.roles || (user?.role && item.roles.includes(user.role))
        );
    }, [user?.role]);

    // Admin-only entries get their own group instead of running on from the daily ones.
    const primaryItems = navItems.filter(item => !item.roles);
    const adminItems = navItems.filter(item => !!item.roles);

    // Signing out is worth a confirm — it is one click away from the account row
    // and it throws away unsaved work on the screen behind. The browser's own
    // confirm() cannot be styled, blocks the page, and looks like a phishing
    // prompt on the web, so this is a real dialog.
    const [isLogoutOpen, setIsLogoutOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            setIsLogoutOpen(false);
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <>
        <aside
            className={`
                ${isOpen ? '' : '-translate-x-full md:translate-x-0'}
                fixed inset-y-0 left-0 z-50 flex flex-col
                md:relative md:inset-y-auto md:h-full
                ${isExpanded ? 'w-[252px]' : 'w-[60px]'}
                bg-[#E4E8EC] transition-[width,transform] duration-200 ease-out
            `}
        >
            {/* Brand */}
            <div className={`flex h-[60px] flex-none items-center ${isExpanded ? 'gap-2.5 pl-[18px] pr-3.5' : 'justify-center'}`}>
                {/* The mark doubles as the collapse toggle: on hover it swaps to the
                    panel glyph, so the rail needs no separate control taking up a row.
                    On phones there is no rail to collapse to, so it stays a plain mark. */}
                {canCollapse ? (
                    <button
                        type="button"
                        onClick={handleToggleCollapse}
                        className="group/brand flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#EEF1F5] transition-colors hover:bg-[#DCE2E8]"
                        title={isExpanded ? 'ย่อแถบข้าง' : 'ขยายแถบข้าง'}
                        aria-label={isExpanded ? 'ย่อแถบข้าง (โลโก้)' : 'ขยายแถบข้าง (โลโก้)'}
                        aria-expanded={isExpanded}
                    >
                        <KoMoldLogo className="block h-[19px] w-[19px] group-hover/brand:hidden" />
                        {isExpanded ? (
                            <PanelLeftClose className="hidden h-[17px] w-[17px] text-[#4E5964] group-hover/brand:block" strokeWidth={1.75} />
                        ) : (
                            <PanelLeftOpen className="hidden h-[17px] w-[17px] text-[#4E5964] group-hover/brand:block" strokeWidth={1.75} />
                        )}
                    </button>
                ) : (
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#EEF1F5]">
                        <KoMoldLogo className="h-[19px] w-[19px]" />
                    </span>
                )}
                {isExpanded && (
                    <>
                        <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-[13.5px] font-bold leading-tight tracking-[-0.01em] text-[#0E1419]">KoMold</span>
                            <span className="truncate text-[10px] font-semibold uppercase tracking-[0.09em] text-[#8892A0]">{appName}</span>
                        </span>
                        {/* The mark toggles too, but nothing about a logo says it is a
                            control. This is the visible one, on the title's line. */}
                        {canCollapse && (
                            <button
                                type="button"
                                onClick={handleToggleCollapse}
                                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-[#D3D9DF] bg-white text-[#4E5964] transition-colors hover:bg-[#F2F5F7] hover:text-[#0E1419]"
                                title="ย่อแถบข้าง"
                                aria-label="ย่อแถบข้าง"
                                aria-expanded={isExpanded}
                            >
                                <PanelLeftClose className="h-[17px] w-[17px]" strokeWidth={1.75} />
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Navigation */}
            <nav className={`flex flex-1 flex-col gap-px overflow-y-auto ${isExpanded ? 'px-2.5' : 'items-center px-0'}`}>
                {primaryItems.map((item) => (
                    <SidebarNavItem key={item.to} {...item} isExpanded={isExpanded} onNavigate={onNavigate} />
                ))}

                {adminItems.length > 0 && (
                    <>
                        {isExpanded ? (
                            <div className="px-[11px] pb-1.5 pt-[18px] text-[11px] font-semibold text-[#9AA3AF]">
                                ผู้ดูแลระบบ
                            </div>
                        ) : (
                            <div className="my-2 h-px w-[22px] flex-none bg-[#C8D0D8]" />
                        )}
                        {adminItems.map((item) => (
                            <SidebarNavItem key={item.to} {...item} isExpanded={isExpanded} onNavigate={onNavigate} />
                        ))}
                    </>
                )}
            </nav>

            {/* Account — the only place logout lives */}
            {!isExpanded && (
                <div className="flex flex-none flex-col items-center gap-1 pb-1">
                    {/* Grouped with the rail's other chrome rather than sitting under
                        the brand: up there it read as the first item of the nav. */}
                    {canCollapse && (
                        <button
                            type="button"
                            onClick={handleToggleCollapse}
                            className="flex h-11 w-11 items-center justify-center rounded-lg text-[#8892A0] transition-colors hover:bg-[#0E1419]/[0.055] hover:text-[#0E1419]"
                            title="ขยายแถบข้าง"
                            aria-label="ขยายแถบข้าง"
                            aria-expanded={isExpanded}
                        >
                            <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.75} />
                        </button>
                    )}
                    <NotificationDropdown
                        className="flex h-11 w-11 items-center justify-center rounded-lg text-[#8892A0] transition-colors hover:bg-[#0E1419]/[0.055] hover:text-[#0E1419]"
                        iconClassName="h-4 w-4"
                    />
                </div>
            )}

            {/* The account row carried notifications and logout but no way to reach
                the profile — the topbar's account menu had โปรไฟล์ของฉัน and it was
                lost when that menu moved here. The avatar and name are the link,
                which is where people look for it. */}
            <div className={`flex h-[60px] flex-none items-center ${isExpanded ? 'gap-2.5 pl-[18px] pr-2.5' : 'justify-center'}`}>
                <Link
                    to={ROUTES.MY_PROFILE}
                    onClick={onNavigate}
                    title={isExpanded ? undefined : 'โปรไฟล์ของฉัน'}
                    aria-label={isExpanded ? undefined : 'โปรไฟล์ของฉัน'}
                    // Expanded, the name block below carries the current-page
                    // marker; collapsed, the avatar is the only link there is.
                    aria-current={isProfileActive && !isExpanded ? 'page' : undefined}
                    className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg text-[12px] font-bold transition-colors ${
                        isProfileActive
                            ? 'bg-[#0E1419] text-white'
                            : 'bg-[#D3D9DF] text-[#0E1419] hover:bg-[#C3CBD4]'
                    }`}
                >
                    {(user?.full_name || '?').charAt(0).toUpperCase()}
                </Link>
                {isExpanded && user && (
                    <>
                        <Link
                            to={ROUTES.MY_PROFILE}
                            onClick={onNavigate}
                            title="โปรไฟล์ของฉัน"
                            aria-current={isProfileActive ? 'page' : undefined}
                            className={`flex min-w-0 flex-1 flex-col rounded-lg px-1 py-1 -mx-1 transition-colors ${
                                isProfileActive ? 'bg-[#0E1419]/[0.075]' : 'hover:bg-[#0E1419]/[0.055]'
                            }`}
                        >
                            <span className="truncate text-[12.5px] font-medium text-[#0E1419]">{user.full_name}</span>
                            <span className="truncate text-[10px] font-semibold uppercase tracking-[0.09em] text-[#8892A0]">
                                {user.role.toLowerCase()}
                            </span>
                        </Link>
                        <NotificationDropdown
                            className="flex h-11 w-11 flex-none items-center justify-center rounded-lg text-[#8892A0] transition-colors hover:bg-[#0E1419]/[0.055] hover:text-[#0E1419] md:h-8 md:w-8"
                            iconClassName="h-[18px] w-[18px] md:h-4 md:w-4"
                        />
                        <button
                            onClick={() => setIsLogoutOpen(true)}
                            className="flex h-11 w-11 flex-none items-center justify-center rounded-lg text-[#8892A0] transition-colors hover:bg-[#0E1419]/[0.055] hover:text-[#0E1419] md:h-8 md:w-8"
                            title="ออกจากระบบ"
                        >
                            <LogOut className="h-[18px] w-[18px] md:h-4 md:w-4" strokeWidth={1.75} />
                        </button>
                    </>
                )}
            </div>

            {isExpanded && (version || commitHash) && (
                <div className="flex-none px-[18px] pb-2.5 text-[10px] tabular-nums text-[#A7B0B9]">
                    {version && <span>v{version}</span>}
                    {version && commitHash && <span> · </span>}
                    {commitHash && <span>{commitHash}</span>}
                </div>
            )}
        </aside>

        <Dialog open={isLogoutOpen} onOpenChange={(open) => !isLoggingOut && setIsLogoutOpen(open)}>
            <DialogContent className="max-w-[400px] rounded-[20px] font-noto-serif-thai">
                <DialogHeader>
                    <DialogTitle className="text-[17px] font-semibold text-[#0E1419]">
                        ออกจากระบบ
                    </DialogTitle>
                    <DialogDescription className="pt-1 text-[14px] leading-relaxed text-[#6B7280]">
                        {user?.full_name
                            ? `ต้องการออกจากระบบของ ${user.full_name} ใช่หรือไม่? คุณจะต้องเข้าสู่ระบบใหม่เพื่อใช้งานต่อ`
                            : 'ต้องการออกจากระบบใช่หรือไม่? คุณจะต้องเข้าสู่ระบบใหม่เพื่อใช้งานต่อ'}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-2">
                    <button
                        type="button"
                        onClick={() => setIsLogoutOpen(false)}
                        disabled={isLoggingOut}
                        className="h-11 w-full rounded-lg border border-[#DDE2E7] bg-white px-4 text-[13.5px] font-semibold text-[#0E1419] transition-colors hover:bg-[#F2F5F7] focus:outline-none focus:ring-2 focus:ring-[#1B4B8F] focus:ring-offset-2 disabled:opacity-50 sm:h-9 sm:w-auto"
                    >
                        ยกเลิก
                    </button>
                    <button
                        type="button"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-[#1B4B8F] px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-[#123464] focus:outline-none focus:ring-2 focus:ring-[#1B4B8F] focus:ring-offset-2 disabled:opacity-60 sm:h-9 sm:w-auto"
                    >
                        {isLoggingOut && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                        {isLoggingOut ? 'กำลังออกจากระบบ...' : 'ออกจากระบบ'}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default ModernSidebar;
